import { useCallback, useEffect, useRef } from "react";
import { useLessonVoiceContext } from "@/providers/LessonVoiceProvider";
import {
  getLessonMoment,
  getLessonMomentFor,
  getLessonMomentIds,
  type LessonVoiceStage,
} from "@/lib/audio/lessonVoiceMap";
import { lessonVoice } from "@/lib/audio/lessonVoiceController";

/**
 * Lesson voice-moment hook.
 *
 * `playLessonMoment(momentId)` — fires once per session per momentId.
 * Safe to call from event handlers. Soft-fails when audio is unavailable.
 *
 * @deprecated Import from `@/hooks/useLessonVoiceMoment` instead. The
 * legacy `useLessonInterjection` name is preserved only for backward
 * compatibility during the terminology migration.
 */
export function useLessonInterjection() {
  const ctx = useLessonVoiceContext();
  return {
    enabled: ctx.enabled,
    setEnabled: ctx.setEnabled,
    volume: ctx.volume,
    setVolume: ctx.setVolume,
    speakingMomentId: ctx.speakingMomentId,
    playLessonMoment: ctx.playMoment,
    preload: ctx.preload,
    stop: ctx.stop,
    getLessonMoment,
    getLessonMomentFor,
  };
}

/**
 * Fires the given lesson voice moment once when `condition` first becomes
 * true. Never replays on rerender or scroll. Unknown momentIds soft-fail.
 *
 * @deprecated Import `useTriggerLessonVoiceMoment` from
 * `@/hooks/useLessonVoiceMoment` instead.
 */
export function useTriggerLessonInterjection(
  momentId: string | null | undefined,
  condition: boolean,
) {
  const { playLessonMoment } = useLessonInterjection();
  const firedRef = useRef(false);
  const pendingRef = useRef(false);
  useEffect(() => {
    if (!momentId) return;
    if (firedRef.current || pendingRef.current) return;
    if (!condition) return;
    pendingRef.current = true;
    void playLessonMoment(momentId)
      .then((result) => {
        if (["played", "suppressed", "already_played"].includes(result.status)) {
          firedRef.current = true;
        }
      })
      .finally(() => { pendingRef.current = false; });
  }, [condition, momentId, playLessonMoment]);
}

/**
 * Preload + reset-per-lesson hook.
 * Warms signed-URL cache for every voice moment in a lesson, and clears
 * fire-once tracking when the active lesson changes so re-entering a
 * lesson can play its lines fresh.
 *
 * @deprecated Import `useLessonVoiceMomentLifecycle` from
 * `@/hooks/useLessonVoiceMoment` instead.
 */
export function useLessonInterjectionLifecycle(lessonId: string | undefined | null) {
  const { preload } = useLessonInterjection();
  const lastLessonRef = useRef<string | null>(null);
  const playMomentsCallback = useCallback(() => getLessonMomentIds(lessonId), [lessonId]);

  useEffect(() => {
    if (!lessonId) return;
    if (lastLessonRef.current && lastLessonRef.current !== lessonId) {
      // New lesson → clear played-state for prior lesson's moments so
      // returning to it later (rare) can re-fire cleanly.
      lessonVoice.resetMoments(getLessonMomentIds(lastLessonRef.current));
    }
    lastLessonRef.current = lessonId;
    const ids = playMomentsCallback();
    if (ids.length) preload(ids);
  }, [lessonId, preload, playMomentsCallback]);
}

export type { LessonVoiceStage };

// ─── Voice-moments terminology aliases ──────────────────────────────
// New code should import these names. The `useLessonInterjection` /
// `useTriggerLessonInterjection` names above remain exported so the
// migration can be gradual without breaking existing call sites.
export const useLessonVoiceMoment = useLessonInterjection;
export const useTriggerLessonVoiceMoment = useTriggerLessonInterjection;
export const useLessonVoiceMomentLifecycle = useLessonInterjectionLifecycle;